// src/pages/Users/Galleries/CreateAlbumModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { FaSave, FaTimes } from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { addGallery } from "../../../services/gallerieServices";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const quillModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};
const quillFormats = ["header", "bold", "italic", "underline", "list", "bullet", "link", "clean"];

function stripHtml(html = "") {
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || div.innerText || "").trim();
}

export default function CreateAlbumModal({ isOpen, onClose, refreshList }) {
  const initial = useMemo(
    () => ({
      titre: "",
      description: "",
      is_active: true, // nouvel album visible par défaut
    }),
    []
  );

  const [formData, setFormData] = useState(initial);
  const [saving, setSaving] = useState(false);
  const firstFieldRef = useRef(null);

  // ouvertures/fermetures : lock scroll + Esc + focus
  useEffect(() => {
    if (!isOpen) return;
    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && safeClose();
    window.addEventListener("keydown", onKey);
    const t = setTimeout(() => firstFieldRef.current?.focus(), 50);
    return () => {
      window.removeEventListener("keydown", onKey);
      root.style.overflow = prevOverflow;
      clearTimeout(t);
    };
    // eslint-disable-next-line
  }, [isOpen]);

  if (!isOpen) return null;

  const isDirty =
    formData.titre !== initial.titre ||
    formData.description !== initial.description ||
    formData.is_active !== initial.is_active;

  const descText = stripHtml(formData.description);
  const descChars = descText.length;

  const onBackdrop = (e) => {
    if (e.target === e.currentTarget) safeClose();
  };

  const safeClose = () => {
    if (saving) return;
    if (!isDirty || window.confirm("Des informations ne sont pas enregistrées. Quitter ?")) {
      onClose?.();
      // reset pour prochaine ouverture
      setTimeout(() => setFormData(initial), 0);
    }
  };

  const onChangeField = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const onChangeContent = (value) => {
    setFormData((prev) => ({ ...prev, description: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const titre = (formData.titre || "").trim();
    if (!titre) {
      toast.warn("Le titre est requis.");
      firstFieldRef.current?.focus();
      return;
    }
    try {
      setSaving(true);
      await addGallery({ ...formData, titre });
      toast.success("Album créé avec succès.");
      await refreshList?.();
      onClose?.();
      setFormData(initial);
    } catch (err) {
      console.error("Erreur création album:", err);
      toast.error("Erreur lors de la création de l’album.");
    } finally {
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-3 backdrop-blur-sm"
      onClick={onBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Créer un album"
    >
      <div
        className="w-full max-w-2xl rounded-2xl overflow-hidden shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200 dark:ring-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 py-3 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white flex items-center justify-between">
          <h3 className="font-semibold">Créer un album</h3>
          <button
            onClick={safeClose}
            title="Fermer"
            aria-label="Fermer"
            className="p-2 rounded-full hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/70"
          >
            <FaTimes />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={onSubmit} className="p-4 sm:p-6 space-y-4">
          {/* Titre */}
          <div>
            <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
              Titre de l’album <span className="text-red-500">*</span>
            </label>
            <input
              ref={firstFieldRef}
              type="text"
              name="titre"
              maxLength={120}
              placeholder="Ex.: Tournoi régional — Janvier 2025"
              value={formData.titre}
              onChange={onChangeField}
              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              required
            />
            <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
              {formData.titre.length}/120 caractères
            </div>
          </div>

          {/* Description (Quill) */}
          <div>
            <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
              Description (optionnelle)
            </label>
            <div className="rounded-xl overflow-hidden ring-1 ring-slate-300 dark:ring-slate-700">
              <ReactQuill
                value={formData.description}
                onChange={onChangeContent}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Décrivez l’événement (contexte, lieu, résultats, remerciements, etc.)"
                className="bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
              />
            </div>
            <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
              {descChars} caractère{descChars > 1 ? "s" : ""} (texte brut)
            </div>
          </div>

          {/* Actif (switch) */}
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-slate-800 dark:text-slate-100">
              Visibilité
            </label>
            <button
              type="button"
              onClick={() => setFormData((p) => ({ ...p, is_active: !p.is_active }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition
                ${formData.is_active ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-700"}`}
              aria-pressed={formData.is_active}
              aria-label="Basculer l’état actif"
              title={formData.is_active ? "Actif" : "Inactif"}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition
                  ${formData.is_active ? "translate-x-5" : "translate-x-1"}`}
              />
            </button>
            <span className="text-sm text-slate-700 dark:text-slate-300">
              {formData.is_active ? "Album visible" : "Album masqué"}
            </span>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              text="Annuler"
              onClick={safeClose}
              icon={FaTimes}
              type="button"
              className="gap-2 rounded-xl"
            />
            <Button
              text={saving ? "Création…" : "Créer l’album"}
              icon={FaSave}
              type="submit"
              className="gap-2 rounded-xl"
              disabled={saving || !isDirty || !formData.titre.trim()}
            />
          </div>

          {/* Hint état */}
          <div className="text-[11px] text-slate-500 dark:text-slate-400">
            {saving
              ? "Envoi en cours…"
              : !isDirty
              ? "Renseignez au moins un champ pour activer la création."
              : "Prêt à créer."}
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
